import React, { useState, useCallback, type ReactNode, useEffect } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import { token } from '@atlaskit/tokens';
import { useIntl, type MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { InfoButton } from '../info-button';

type Props = {
	children: ReactNode;
	heading: MessageDescriptor;
	expandedHeading?: MessageDescriptor;
	infoHeading?: string | ReactNode;
	infoDescription?: string | ReactNode;
	onCloseClick?: () => void;
	testId?: string;
	shouldForceOpen?: boolean;
	ariaRoleDescription?: MessageDescriptor;
};

export const Accordion = ({
	children,
	heading,
	expandedHeading,
	infoHeading,
	infoDescription,
	onCloseClick,
	testId,
	shouldForceOpen = false,
	ariaRoleDescription,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'newProjectCreateMoreOptions',
			}),
		);
		if (isOpen && onCloseClick) {
			onCloseClick();
			return;
		}
		setIsOpen(!isOpen);
	}, [createAnalyticsEvent, isOpen, onCloseClick]);

	useEffect(() => {
		shouldForceOpen && setIsOpen(true);
	}, [shouldForceOpen]);

	return (
		<Wrapper>
			<HeadingWrapper>
				<Button
					aria-roledescription={ariaRoleDescription && formatMessage(ariaRoleDescription)}
					isDisabled={shouldForceOpen}
					testId={testId}
					onClick={handleClick}
					appearance="link"
					spacing="none"
					iconBefore={isOpen ? <ChevronDownIcon label="" /> : <ChevronRightIcon label="" />}
				>
					{isOpen && expandedHeading ? formatMessage(expandedHeading) : formatMessage(heading)}
				</Button>
				{infoHeading && infoDescription && (
					<InfoButton heading={infoHeading} description={infoDescription} />
				)}
			</HeadingWrapper>
			{isOpen && <ChildrenWrapper>{children}</ChildrenWrapper>}
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ChildrenWrapper = styled.div({
	display: 'flex',
	marginTop: token('space.100', '8px'),
	alignItems: 'baseline',
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> button': {
		width: 'fit-content',
		alignItems: 'center',
		marginLeft: token('space.050', '4px'),
	},
});
